import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { integrationsService } from '@/services/integrations'
import { guardianService } from '@/services/guardian'

type CallbackStatus = 'processing' | 'success' | 'error'

export function OAuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<CallbackStatus>('processing')
  const [errorMessage, setErrorMessage] = useState('')

  // Popups (opened via window.open) can auto-close; a plain tab (e.g. a link
  // opened from a chat/agent) cannot — window.close() is blocked there, so we
  // show a "you can close this tab" hint instead of claiming it closes itself.
  const hasOpener = typeof window !== 'undefined' && !!window.opener

  useEffect(() => {
    console.log('=== OAUTH CALLBACK PAGE LOADED ===')
    console.log('Current URL:', window.location.href)
    console.log('Search params:', Object.fromEntries(searchParams))
    console.log('State param:', searchParams.get('state'))
    console.log('Code param:', searchParams.get('code'))
    console.log('Window opener exists:', !!window.opener)
    console.log('Window opener origin:', window.opener ? window.location.origin : 'No opener')
    console.log('===================================')

    // Check if this is a Guardian OAuth callback
    const state = searchParams.get('state')
    if (state) {
      try {
        const stateData = JSON.parse(atob(state))
        if (stateData.source === 'guardian') {
          console.log('Guardian OAuth callback detected')
          handleGuardianCallback(state, searchParams.get('code') || '')
          return
        }
      } catch {
        // Not base64 JSON — proceed with standard flow
      }
    }

    // Standard integration OAuth callback
    exchangeToken()
  }, [])

  const fail = (message: string) => {
    setErrorMessage(message)
    setStatus('error')
  }

  const handleGuardianCallback = async (state: string, code: string) => {
    if (!code) {
      fail('Missing authorization code.')
      if (window.opener) {
        window.opener.postMessage({ source: 'guardian-oauth', success: false }, window.location.origin)
      }
      if (hasOpener) setTimeout(() => window.close(), 1000)
      return
    }

    try {
      const result = await guardianService.oauthCallback(code, state)
      if (result.success) {
        setStatus('success')
      } else {
        fail('The provider did not grant access.')
      }
      if (window.opener) {
        window.opener.postMessage({ source: 'guardian-oauth', success: result.success }, window.location.origin)
      }
      if (hasOpener) setTimeout(() => window.close(), 500)
    } catch (error) {
      console.error('Guardian OAuth error:', error)
      fail('Authorization failed. Please try again.')
      if (window.opener) {
        window.opener.postMessage({ source: 'guardian-oauth', success: false }, window.location.origin)
      }
      if (hasOpener) setTimeout(() => window.close(), 1000)
    }
  }

  const exchangeToken = async () => {
    console.log('=== STARTING OAUTH TOKEN EXCHANGE ===')

    const state = searchParams.get('state')
    const code = searchParams.get('code')

    console.log('State:', state)
    console.log('Code:', code)

    if (!state || !code) {
      console.error('Missing state or code parameters')
      fail('Missing OAuth parameters.')
      if (window.opener) {
        window.opener.postMessage({
          type: 'OAUTH_ERROR',
          data: {
            message: 'Missing OAuth parameters',
            status: 400
          }
        }, window.location.origin)
      }
      if (hasOpener) setTimeout(() => window.close(), 1000)
      return
    }

    try {
      console.log('OAuth popup processing - isolated from main window auth state')

      // OAuth popups should not interfere with main window authentication
      // The auth context is already bypassed for popups, so we can proceed safely
      console.log('Calling OAuth callback with authenticated API service...')
      const resp = await integrationsService.oauthCallback(state, code)
      console.log('OAuth API response:', resp)

      if (resp.status === 201) {
        console.log('OAuth callback successful:', resp.data)
        setStatus('success')

        // Send success message to parent window
        if (window.opener) {
          console.log('Sending success message to parent window')
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              integrationId: resp.data?.id,
              status: resp.data?.status,
              message: 'OAuth authorization successful'
            }
          }, window.location.origin)
        } else {
          console.warn('No window.opener found - cannot notify parent')
        }

        // Close popup window (no-op in a plain tab)
        if (hasOpener) {
          console.log('Closing popup window in 500ms')
          setTimeout(() => window.close(), 500)
        }
      } else {
        console.error('Unexpected response status:', resp.status)
        fail(`Unexpected response from the server (status ${resp.status}).`)
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_ERROR',
            data: {
              message: `Unexpected response status: ${resp.status}`,
              status: resp.status
            }
          }, window.location.origin)
        }
        if (hasOpener) setTimeout(() => window.close(), 1000)
      }
    } catch (error: any) {
      console.error('OAuth callback error:', error)
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })

      fail(error.response?.data?.message || error.message || 'OAuth authorization failed.')

      // Send error message to parent window
      if (window.opener) {
        console.log('Sending error message to parent window')
        window.opener.postMessage({
          type: 'OAUTH_ERROR',
          data: {
            message: error.response?.data?.message || error.message || 'OAuth authorization failed',
            status: error.response?.status || 500
          }
        }, window.location.origin)
      } else {
        console.warn('No window.opener found - cannot notify parent of error')
      }

      // Close window after delay (no-op in a plain tab)
      if (hasOpener) {
        console.log('Closing popup window in 1000ms due to error')
        setTimeout(() => window.close(), 1000)
      }
    }
  }

  const closeHint = hasOpener
    ? 'This window will close automatically.'
    : 'You can close this tab and return to Glaium.'

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      padding: '24px'
    }}>
      <div style={{ textAlign: 'center', maxWidth: '420px' }}>
        {status === 'processing' && (
          <>
            <h2>Finishing connection…</h2>
            <p>Please wait while we complete the authorization.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 style={{ color: '#137333' }}>✓ Your account is connected</h2>
            <p style={{ fontSize: '13px', color: '#666', marginTop: '16px' }}>
              {closeHint}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 style={{ color: '#b00020' }}>⚠ Authorization failed</h2>
            <p>{errorMessage || 'Something went wrong completing the authorization.'}</p>
            <p style={{ fontSize: '13px', color: '#666', marginTop: '16px' }}>
              {hasOpener ? 'This window will close automatically.' : 'You can close this tab and try again.'}
            </p>
          </>
        )}
      </div>
    </div>
  )
}
